import { useCallback, useEffect, useMemo, useState } from 'react'
import { KeyRound, Pencil, Plus, ShieldOff, ShieldCheck, Trash2 } from 'lucide-react'
import type { ConsoleUser, UserAccess } from './types'
import {
  createConsoleUser, deleteConsoleUser, deleteUserAccess, disableConsoleUser, enableConsoleUser,
  fetchConsoleUsers, fetchUserAccess, renameConsoleUser, resetConsoleUserPassword,
  updateConsoleUserRole, upsertUserAccess,
} from './useAdminApi'
import { ConfirmDialog, SettingsFormModal } from './SettingsFormModal'
import { AdminSearch, AdminTable, Banner, Btn, Field, FilterMenu, PasswordField, PasswordInput, SectionHeader, Td, Th, inputCls } from './ui'
import { DEPARTMENTS, DEPARTMENT_LABEL, DepartmentPicker, SITES, SiteMultiSelect } from './accessPickers'

const ROLES = [
  { id: 'user', label: 'User', hint: 'sign in, use the console' },
  { id: 'admin', label: 'Admin', hint: 'also change thresholds, geofences, TAT targets' },
  { id: 'super_admin', label: 'Super admin', hint: 'also create, disable and delete accounts' },
] as const

const ROLE_LABEL: Record<string, string> = {
  user: 'User', admin: 'Admin', super_admin: 'Super admin',
}

/** Shared by the New-user and Edit-access modals -- same vocabulary as accessPickers'
 *  DEPARTMENTS, plus a leading "skip" entry, since a grant isn't always wanted here
 *  (e.g. IT-Support relies on the admin role for full access; Management gets it
 *  automatically by department alone -- see accessPickers.tsx's own DEPARTMENTS
 *  hints and AccessSection.tsx). */
const GRANT_DEPARTMENTS = [
  { id: '', label: 'No access grant', hint: "Add one later from Department & site access" },
  ...DEPARTMENTS,
] as const

const ADMIN_ROLES = new Set(['admin', 'super_admin'])

/** Same rule serving/auth.py validate_username enforces: lowercase, 3–32, no spaces. */
const USERNAME_RE = /^[a-z0-9][a-z0-9._-]{2,31}$/
const MIN_PASSWORD_LEN = 10
const USERNAME_HINT = '3–32 characters: letters, digits, dot, dash or underscore. No spaces.'

const ROW_ACTION =
  'inline-flex h-11 items-center gap-1 rounded-lg px-2 text-xs font-medium'

/** Picking a department with zero sites selected is a silent total lockout for a
 *  plain `user` role -- serving/auth.py's check_site(user, site) has no "empty means
 *  all" case, and the only exemptions are `user.is_admin`, i.e. role in {admin,
 *  super_admin} (check_resource()'s own DEPARTMENT_GRANTS table is a SEPARATE
 *  resource-key grant, never consulted by check_site, so it cannot exempt anyone from
 *  this). Round-2 adversarial review (HIGH): an earlier version of this guard exempted
 *  it_support/management by DEPARTMENT, which still let a plain-role it_support
 *  account save with zero sites and get resource access to a site list it can never
 *  actually read from -- the exemption had to follow ROLE, not department, for
 *  it_support.
 *
 *  management is the one deliberate exception to that rule now: department alone
 *  flips is_admin server-side for management (serving/auth.py's
 *  DEPARTMENT_GRANTS_ADMIN -- direct request, "let management see everything, have
 *  full access"), so a plain-role management account really is exempt, the same way
 *  an explicit admin role already is. it_support got no such change and still needs
 *  the role. */
/** Shared by siteLockoutRisk above and the Sites-picker visibility below -- an admin
 *  role or a management department both already have every site server-side, so the
 *  picker would be acting on a selection check_site never consults either way. */
function hasFullSiteAccess(department: string, role: string): boolean {
  return ADMIN_ROLES.has(role) || department === 'management'
}

function siteLockoutRisk(department: string, sites: string[], role: string): boolean {
  return department !== '' && !hasFullSiteAccess(department, role) && sites.length === 0
}

const EMPTY = {
  username: '', password: '', role: 'user' as string,
  department: '' as string, sites: [] as string[],
}

function userMatches(
  u: ConsoleUser,
  grant: UserAccess | undefined,
  query: string,
  role: string,
  department: string,
  status: string,
  site: string,
): boolean {
  if (role !== 'all' && u.role !== role) return false
  if (status === 'active' && u.disabled) return false
  if (status === 'inactive' && !u.disabled) return false
  const dept = grant?.department ?? ''
  if (department === 'none' && dept !== '') return false
  if (department !== 'all' && department !== 'none' && dept !== department) return false
  const sites = grant?.sites ?? []
  if (site !== 'all' && !sites.includes(site)) return false
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const hay = [
    u.username,
    u.created_by ?? '',
    ROLE_LABEL[u.role] ?? u.role,
    u.role,
    dept,
    DEPARTMENT_LABEL[dept] ?? '',
    ...sites,
  ]
  return hay.some((h) => h.toLowerCase().includes(needle))
}

function sameSites(a: string[], b: string[]): boolean {
  // Set-based, not length+every -- a duplicate entry (SiteMultiSelect's own toggle
  // can't create one, but serving/auth.py's validate_sites doesn't dedup either, so a
  // stored row reached some other way could carry one) made the original length+every
  // version both asymmetric and wrong: ['a','a'] vs ['a','b'] read as "same".
  const sa = new Set(a)
  const sb = new Set(b)
  return sa.size === sb.size && [...sa].every((s) => sb.has(s))
}

/** Shared by the "New user" and "Edit access" modals -- both are just this picker plus
 *  whatever else that flow needs around it. */
function RolePicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  return (
    <div className="grid gap-1.5">
      {ROLES.map((r) => (
        <button
          key={r.id} type="button" onClick={() => onChange(r.id)}
          className={
            'rounded-lg border px-3 py-2 text-left transition-colors ' +
            (value === r.id
              ? 'border-blue-400 bg-blue-50'
              : 'border-slate-200 bg-white hover:bg-slate-50')
          }
        >
          <span className="block text-sm font-medium text-slate-900">{r.label}</span>
          <span className="block text-xs text-slate-500">{r.hint}</span>
        </button>
      ))}
    </div>
  )
}

/** Super-admin only -- the server enforces this too (403 on every /api/auth/users route
 *  for anyone else), so a stale client that somehow renders this tab still cannot act on
 *  it. Create/list/disable/enable were built with the local-auth system; this is the
 *  first UI in front of them. */
export function UsersSection({
  isSuperAdmin, currentUsername = '',
}: {
  isSuperAdmin: boolean
  currentUsername?: string
}) {
  const [rows, setRows] = useState<ConsoleUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'error'; msg: string } | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [query, setQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [departmentFilter, setDepartmentFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [siteFilter, setSiteFilter] = useState('all')
  const [pendingDelete, setPendingDelete] = useState<ConsoleUser | null>(null)
  const [editingUser, setEditingUser] = useState<ConsoleUser | null>(null)
  const [editRole, setEditRole] = useState('user')
  const [accessRows, setAccessRows] = useState<UserAccess[]>([])
  const [accessError, setAccessError] = useState<string | null>(null)
  const [editDepartment, setEditDepartment] = useState('')
  const [editSites, setEditSites] = useState<string[]>([])
  const [editHadGrant, setEditHadGrant] = useState(false)
  // The grant as it was WHEN THE MODAL OPENED -- saveEdit diffs against this, not
  // against editHadGrant alone, so a role-only edit (department/sites left untouched)
  // never re-sends upsertUserAccess at all. Without this, every save silently rewrote
  // the grant's updated_at/updated_by to whoever last touched the user's ROLE, and could
  // fail the whole save with an "access grant failed" error for an admin who never
  // intended to touch access.
  const [editInitialDepartment, setEditInitialDepartment] = useState('')
  const [editInitialSites, setEditInitialSites] = useState<string[]>([])
  const [editUsername, setEditUsername] = useState('')
  const [pendingReset, setPendingReset] = useState<ConsoleUser | null>(null)
  const [resetPassword, setResetPassword] = useState('')
  const [resetConfirm, setResetConfirm] = useState('')
  const [resetError, setResetError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setAccessError(null)
    try {
      // allSettled, not all -- the users table is this screen's PRIMARY capability and
      // must render even if the access-grant fetch fails (a missing/unavailable
      // user_access store must not read as "you have no accounts" to a super_admin who
      // may need to disable one during exactly that kind of incident).
      const [users, access] = await Promise.allSettled([fetchConsoleUsers(), fetchUserAccess()])
      if (users.status === 'fulfilled') {
        setRows(users.value)
      } else {
        setError(users.reason instanceof Error ? users.reason.message : 'Failed to load accounts')
      }
      if (access.status === 'fulfilled') {
        setAccessRows(access.value)
      } else {
        setAccessRows([])
        const reason = access.reason instanceof Error ? access.reason.message : 'unknown error'
        setAccessError(`Access grants unavailable (${reason}) -- department/sites editing is disabled until this loads.`)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const openNew = () => { setForm(EMPTY); setCreating(true) }

  const save = async () => {
    if (saving) return
    if (!form.username.trim() || !form.password.trim()) {
      setBanner({ tone: 'error', msg: 'Username and password are both required' })
      return
    }
    if (siteLockoutRisk(form.department, form.sites, form.role)) {
      setBanner({ tone: 'error', msg: 'Pick at least one site, or give them the Admin/Super admin role for full access.' })
      return
    }
    const username = form.username.trim()
    setSaving(true)
    try {
      try {
        await createConsoleUser(username, form.password, form.role)
      } catch (e) {
        setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Create failed' })
        return
      }
      // Account exists past this point -- a failed grant below is reported as a partial
      // success, not rolled back, since createConsoleUser already committed.
      if (form.department) {
        try {
          const saved = await upsertUserAccess(username.toLowerCase(), form.department, form.sites)
          setBanner({ tone: 'ok', msg: `Account "${username}" created with access grant for "${saved.subject}"` })
        } catch (e) {
          setBanner({
            tone: 'error',
            msg: `Account "${username}" created, but its access grant failed: ${e instanceof Error ? e.message : 'unknown error'}. Add it from Department & site access.`,
          })
          setCreating(false)
          await load()
          return
        }
      } else {
        setBanner({ tone: 'ok', msg: `Account "${username}" created` })
      }
      setCreating(false)
      await load()
    } finally {
      setSaving(false)
    }
  }

  const toggle = async (u: ConsoleUser) => {
    try {
      if (u.disabled) await enableConsoleUser(u.id)
      else await disableConsoleUser(u.id)
      setBanner({ tone: 'ok', msg: `"${u.username}" ${u.disabled ? 'reactivated' : 'deactivated'}` })
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Update failed' })
    }
  }

  const confirmDelete = async () => {
    if (!pendingDelete || saving) return
    setSaving(true)
    try {
      await deleteConsoleUser(pendingDelete.id)
      setBanner({ tone: 'ok', msg: `"${pendingDelete.username}" and their permissions were removed` })
      setPendingDelete(null)
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Delete failed' })
    } finally {
      setSaving(false)
    }
  }

  const accessBySubject = useMemo(() => {
    const map = new Map<string, UserAccess>()
    for (const a of accessRows) map.set(a.subject, a)
    return map
  }, [accessRows])

  const visible = useMemo(() => rows.filter((u) => userMatches(
    u,
    accessBySubject.get(u.username.toLowerCase()),
    query,
    roleFilter,
    departmentFilter,
    statusFilter,
    siteFilter,
  )), [rows, accessBySubject, query, roleFilter, departmentFilter, statusFilter, siteFilter])

  const filteredEmpty = !loading && rows.length > 0 && visible.length === 0

  const openEdit = (u: ConsoleUser) => {
    setEditingUser(u)
    setEditUsername(u.username)
    setEditRole(u.role)
    const existing = accessRows.find((a) => a.subject === u.username.toLowerCase())
    const department = existing?.department ?? ''
    const sites = existing?.sites ?? []
    setEditDepartment(department)
    setEditSites(sites)
    setEditHadGrant(existing != null)
    setEditInitialDepartment(department)
    setEditInitialSites(sites)
  }

  const saveEdit = async () => {
    if (!editingUser || saving) return
    const nextUsername = editUsername.trim().toLowerCase()
    if (!USERNAME_RE.test(nextUsername)) {
      setBanner({ tone: 'error', msg: `Username must be ${USERNAME_HINT}` })
      return
    }
    if (siteLockoutRisk(editDepartment, editSites, editRole)) {
      setBanner({ tone: 'error', msg: 'Pick at least one site, or give them the Admin/Super admin role for full access.' })
      return
    }
    const previousUsername = editingUser.username
    setSaving(true)
    try {
      let username = previousUsername
      if (nextUsername !== previousUsername.toLowerCase()) {
        try {
          const renamed = await renameConsoleUser(editingUser.id, nextUsername)
          username = renamed.username
        } catch (e) {
          setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Rename failed' })
          return
        }
      }
      try {
        await updateConsoleUserRole(editingUser.id, editRole)
      } catch (e) {
        setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Role update failed' })
        return
      }
      // Role change is committed past this point -- an access-step failure below is
      // reported as a partial success, not rolled back, same convention save() already
      // uses for New user. Access is only touched when department/sites actually
      // CHANGED from what was showing when this modal opened -- a role-only edit must
      // never silently re-upsert (and so re-stamp updated_at/updated_by on) a grant
      // nobody asked to change. `!accessError` is load-bearing, not defensive: when the
      // access-grant fetch failed, editDepartment/editInitialDepartment are BOTH stuck
      // at "" (never actually read), so an unrelated department picked in a LATER,
      // successfully-loaded session would look "changed" against that fabricated ""
      // baseline -- never write access state built on a prefill we know is unreliable.
      const accessChanged = !accessError
        && (editDepartment !== editInitialDepartment || !sameSites(editSites, editInitialSites))
      let accessNote = ''
      if (accessChanged) {
        try {
          if (editDepartment) {
            const saved = await upsertUserAccess(username.toLowerCase(), editDepartment, editSites)
            accessNote = `, access updated for "${saved.subject}"`
          } else if (editHadGrant) {
            // Department was cleared back to "No access grant" and a grant existed
            // before this modal opened -- actually revoke it, not just stop showing it.
            await deleteUserAccess(username.toLowerCase())
            accessNote = ', access grant removed'
          }
        } catch (e) {
          setBanner({
            tone: 'error',
            msg: `"${username}" is now ${ROLE_LABEL[editRole] ?? editRole}, but its access grant failed to update: ${e instanceof Error ? e.message : 'unknown error'}. Fix it from Department & site access.`,
          })
          setEditingUser(null)
          await load()
          return
        }
      }
      setBanner({ tone: 'ok', msg: `"${username}" is now ${ROLE_LABEL[editRole] ?? editRole}${accessNote}` })
      setEditingUser(null)
      await load()
    } finally {
      setSaving(false)
    }
  }

  const openReset = (u: ConsoleUser) => {
    setPendingReset(u)
    setResetPassword('')
    setResetConfirm('')
    setResetError(null)
  }

  const confirmReset = async () => {
    if (!pendingReset || saving) return
    if (resetPassword.length < MIN_PASSWORD_LEN) {
      setResetError(`Password must be at least ${MIN_PASSWORD_LEN} characters`)
      return
    }
    if (resetPassword !== resetConfirm) {
      setResetError('The two passwords do not match')
      return
    }
    setSaving(true)
    setResetError(null)
    try {
      const out = await resetConsoleUserPassword(pendingReset.id, resetPassword)
      const note = out.reseededNote ? ` ${out.reseededNote}` : ''
      setBanner({
        tone: 'ok',
        msg: `Password reset for "${out.username}". They will need to sign in again.${note}`,
      })
      setPendingReset(null)
      setResetPassword('')
      setResetConfirm('')
      await load()
    } catch (e) {
      setResetError(e instanceof Error ? e.message : 'Password reset failed')
    } finally {
      setSaving(false)
    }
  }

  if (!isSuperAdmin) {
    return (
      <p className="text-sm text-slate-500">
        Account management is limited to the platform's super administrator.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Users"
        subtitle="Who can sign in, and what they can change once they do."
        action={<Btn onClick={openNew}><span className="inline-flex items-center gap-1"><Plus size={14} /> New user</span></Btn>}
      />
      {banner && !((creating || editingUser !== null) && banner.tone === 'error') ? (
        <Banner tone={banner.tone} onDismiss={() => setBanner(null)}>{banner.msg}</Banner>
      ) : null}
      {error ? <Banner tone="error">{error}</Banner> : null}
      {accessError ? <Banner tone="error">{accessError}</Banner> : null}
      <div className="flex flex-wrap items-center gap-2">
        <AdminSearch
          value={query}
          onChange={setQuery}
          label="Search users by username or role"
          placeholder="Search username or role"
        />
        <FilterMenu
          onClear={() => {
            setRoleFilter('all')
            setDepartmentFilter('all')
            setStatusFilter('all')
            setSiteFilter('all')
          }}
          groups={[
            {
              id: 'role', label: 'Role', value: roleFilter, onChange: setRoleFilter,
              options: [{ id: 'all', label: 'All' }, ...ROLES.map((r) => ({ id: r.id, label: r.label }))],
            },
            {
              id: 'department', label: 'Department', value: departmentFilter, onChange: setDepartmentFilter,
              options: [
                { id: 'all', label: 'All' },
                { id: 'none', label: 'No grant' },
                ...DEPARTMENTS.map((d) => ({ id: d.id, label: d.label })),
              ],
            },
            {
              id: 'status', label: 'Status', value: statusFilter, onChange: setStatusFilter,
              options: [
                { id: 'all', label: 'All' },
                { id: 'active', label: 'Active' },
                { id: 'inactive', label: 'Inactive' },
              ],
            },
            {
              id: 'site', label: 'Site', value: siteFilter, onChange: setSiteFilter,
              options: [{ id: 'all', label: 'All' }, ...SITES.map((s) => ({ id: s.id, label: s.label }))],
            },
          ]}
        />
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <AdminTable>
          <thead>
            <tr>
              <Th>Username</Th>
              <Th>Role</Th>
              <Th>Department</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>By</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.map((u) => {
              const grant = accessBySubject.get(u.username.toLowerCase())
              const isSelf = Boolean(currentUsername) && currentUsername.toLowerCase() === u.username.toLowerCase()
              return (
              <tr key={u.id} className={u.disabled ? 'bg-slate-50/60 opacity-60' : 'hover:bg-slate-50/80'}>
                <Td mono>{u.username}</Td>
                <Td>{ROLE_LABEL[u.role] ?? u.role}</Td>
                <Td>{grant ? (DEPARTMENT_LABEL[grant.department] ?? grant.department) : '—'}</Td>
                <Td>
                  <span className={
                    u.disabled
                      ? 'rounded bg-slate-200 px-1.5 py-0.5 text-xs font-medium text-slate-600'
                      : 'rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700'
                  }>
                    {u.disabled ? 'inactive' : 'active'}
                  </span>
                </Td>
                <Td>{new Date(u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</Td>
                <Td>{u.created_by ?? '—'}</Td>
                <Td>
                  <div className="flex flex-wrap items-center gap-1">
                    <button
                      type="button" onClick={() => openEdit(u)}
                      className={`${ROW_ACTION} text-slate-600 hover:bg-slate-100`}
                      title="Edit username, role, department and sites"
                    >
                      <Pencil size={14} />
                      Edit access
                    </button>
                    <button
                      type="button" onClick={() => openReset(u)}
                      className={`${ROW_ACTION} text-slate-600 hover:bg-slate-100`}
                      title="Set a new password and sign them out"
                    >
                      <KeyRound size={14} />
                      Reset password
                    </button>
                    <button
                      type="button" onClick={() => void toggle(u)}
                      className={
                        u.disabled
                          ? `${ROW_ACTION} text-emerald-600 hover:bg-emerald-50`
                          : `${ROW_ACTION} text-rose-500 hover:bg-rose-50`
                      }
                      title={u.disabled ? 'Reactivate' : 'Deactivate'}
                    >
                      {u.disabled ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
                      {u.disabled ? 'Activate' : 'Deactivate'}
                    </button>
                    {isSelf ? null : (
                      <button
                        type="button" onClick={() => setPendingDelete(u)}
                        className={`${ROW_ACTION} text-rose-500 hover:bg-rose-50`}
                        title="Delete user and their permissions"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
              )
            })}
            {!rows.length ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-400">No accounts yet</td></tr>
            ) : filteredEmpty ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-400">No users match this search</td></tr>
            ) : null}
          </tbody>
        </AdminTable>
      )}

      <SettingsFormModal
        open={creating}
        title="New user"
        subtitle="They sign in with this username and password immediately."
        onClose={() => setCreating(false)}
        onSave={() => void save()}
        saving={saving}
        error={banner?.tone === 'error' ? banner.msg : null}
      >
        <div className="grid gap-3">
          <div>
            <Field label="Username">
              <input className={inputCls} value={form.username} autoFocus
                     onChange={(e) => setForm({ ...form, username: e.target.value })} />
            </Field>
            <p className="mt-1 text-[11px] text-slate-500">{USERNAME_HINT}</p>
          </div>
          <Field label="Password">
            <PasswordInput value={form.password} onChange={(password) => setForm({ ...form, password })} autoComplete="new-password" />
          </Field>
          <Field label="Role">
            <RolePicker value={form.role} onChange={(id) => setForm({ ...form, role: id })} />
          </Field>
          <Field label="Department">
            <DepartmentPicker
              items={GRANT_DEPARTMENTS} value={form.department}
              onChange={(id) => setForm({ ...form, department: id })}
            />
          </Field>
          {form.department && !hasFullSiteAccess(form.department, form.role) ? (
            <Field label="Sites">
              <SiteMultiSelect value={form.sites} onChange={(sites) => setForm({ ...form, sites })} />
            </Field>
          ) : null}
          {form.department && hasFullSiteAccess(form.department, form.role) ? (
            <p className="text-xs text-slate-500">
              {ADMIN_ROLES.has(form.role)
                ? 'Admin and Super admin already have every site -- nothing to pick here.'
                : 'Management already has every site -- nothing to pick here.'}
            </p>
          ) : null}
        </div>
      </SettingsFormModal>

      <SettingsFormModal
        open={editingUser !== null}
        title={`Edit access — ${editingUser?.username ?? ''}`}
        subtitle="Username, role, department and sites. Username changes take effect on their next request."
        onClose={() => setEditingUser(null)}
        onSave={() => void saveEdit()}
        saving={saving}
        error={banner?.tone === 'error' ? banner.msg : null}
      >
        <div className="grid gap-3">
          <div>
            <Field label="Username">
              <input className={inputCls} value={editUsername} autoFocus
                     onChange={(e) => setEditUsername(e.target.value)} />
            </Field>
            <p className="mt-1 text-[11px] text-slate-500">{USERNAME_HINT}</p>
          </div>
          <Field label="Role">
            <RolePicker value={editRole} onChange={setEditRole} />
          </Field>
          {accessError ? (
            <p className="text-xs text-rose-600">
              This user's current department/sites couldn't be loaded, so editing them here
              is disabled for this session (saving still updates role only). Reopen this
              dialog once access grants have loaded, or use Department & site access.
            </p>
          ) : (
            <>
              <Field label="Department">
                <DepartmentPicker
                  items={GRANT_DEPARTMENTS} value={editDepartment}
                  onChange={setEditDepartment}
                />
              </Field>
              {editDepartment && !hasFullSiteAccess(editDepartment, editRole) ? (
                <Field label="Sites">
                  <SiteMultiSelect value={editSites} onChange={setEditSites} />
                </Field>
              ) : null}
              {editDepartment && hasFullSiteAccess(editDepartment, editRole) ? (
                <p className="text-xs text-slate-500">
                  {ADMIN_ROLES.has(editRole)
                    ? 'Admin and Super admin already have every site -- nothing to pick here.'
                    : 'Management already has every site -- nothing to pick here.'}
                </p>
              ) : null}
            </>
          )}
        </div>
      </SettingsFormModal>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Delete ${pendingDelete?.username ?? 'user'}?`}
        description={`This removes the account and their department, site, and session permissions. They will not be able to sign in. This cannot be undone.`}
        confirmLabel="Delete user"
        onConfirm={() => void confirmDelete()}
        onClose={() => { if (!saving) setPendingDelete(null) }}
        confirming={saving && pendingDelete !== null}
      />

      <SettingsFormModal
        open={pendingReset !== null}
        title={`Reset password for ${pendingReset?.username ?? 'user'}?`}
        subtitle="They will be signed out of every browser and mobile session. Set a new password of at least 10 characters."
        saveLabel="Reset password"
        onClose={() => { if (!saving) setPendingReset(null) }}
        onSave={() => void confirmReset()}
        saving={saving}
      >
        <div className="grid gap-3">
          {resetError ? <p className="text-sm text-rose-700">{resetError}</p> : null}
          <PasswordField
            label="New password"
            value={resetPassword}
            onChange={(v) => { setResetPassword(v); setResetError(null) }}
            autoComplete="new-password"
            autoFocus
          />
          <PasswordField
            label="Confirm password"
            value={resetConfirm}
            onChange={(v) => { setResetConfirm(v); setResetError(null) }}
            autoComplete="new-password"
          />
        </div>
      </SettingsFormModal>
    </div>
  )
}
